import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface InstagramMessageDialogProps {
  open: boolean;
  onClose: () => void;
  pageId: string;
  pageName: string;
  onSendMessage: (recipientId: string, text: string) => Promise<{ success: boolean; error?: string }>;
}

export const InstagramMessageDialog: React.FC<InstagramMessageDialogProps> = ({
  open,
  onClose,
  pageId,
  pageName,
  onSendMessage
}) => {
  const [recipientId, setRecipientId] = useState('');
  const [message, setMessage] = useState('Test message');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSend = async () => {
    if (!recipientId.trim()) {
      setError('Recipient ID is required');
      return;
    }

    if (!message.trim()) {
      setError('Message content is required');
      return;
    }

    setError(null);
    setSuccess(false);
    setIsSending(true);

    try {
      const result = await onSendMessage(recipientId.trim(), message.trim());
      
      if (result && result.success) {
        setSuccess(true);
        // Reset form after successful send
        setTimeout(() => {
          setRecipientId('');
          setMessage('Test message');
          onClose();
        }, 2000);
      } else {
        setError((result && result.error) || 'Failed to send message - no response from server');
      }
    } catch (err: any) {
      console.error('Error in message dialog:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    if (!isSending) {
      setRecipientId('');
      setMessage('Test message');
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Send Instagram Message</DialogTitle>
          <DialogDescription>
            Send a test message to an Instagram user from your page: {pageName}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="recipientId" className="text-right">
              Recipient ID
            </Label>
            <Input
              id="recipientId"
              placeholder="Instagram user ID"
              className="col-span-3"
              value={recipientId}
              onChange={(e) => setRecipientId(e.target.value)}
              disabled={isSending}
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="message" className="text-right">
              Message
            </Label>
            <Textarea
              id="message"
              placeholder="Enter your message"
              className="col-span-3"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isSending}
              rows={3}
            />
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert variant="default" className="bg-green-50 text-green-800 border-green-200">
            <AlertDescription>Message sent successfully!</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={isSending}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSend} disabled={isSending}>
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Message'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InstagramMessageDialog; 